// The LAST SEASON card of RECORD › OVERVIEW (R3289, ui-map-interface.md
// §16.3).
//
// The season summary card shows a season at its close, inside the game.
// After the roll this card shows the newest closed season of the account
// in short: the places of the commander, of the clan and of the faction,
// the Honour the season paid and the captains who crossed the line. The
// figures come from `GET /api/seasons/last`, which reads the stored season
// report; the game server ranked the clans and the factions of the whole
// table, so the card computes no place.
//
// The presses reach the season chronicle of that season (chronicle.md
// §9): CHRONICLE opens the book on the volume, and SHARE shares the volume
// and hands the address of the public reader on. A season is shared only
// after the roll (§7.3), and this card stands after the roll. A season
// with no volume offers neither press; an account without the `chronicle`
// key gets CHRONICLE alone, which shows the locked card.
//
// THE SEA plays the season timelapse, and every closed season of a public
// mode offers it (R3300): it opens the public reader of a shared volume
// (§9.4), which plays the timelapse beside the pages, and else the sea
// page (§9.5), which needs no share, because a closed season's territory
// map names no fleet and no player (§10). The chronicle key and the share
// gate the facts of the volume, never the map.
//
// An account with no closed season gets no card.

import type { ChronicleVolume, LastSeason } from '../../../net/api';
import { MODE_ARCADE, MODE_CUSTOM } from '../../../gen/balance';
import { button, linkButton, tag } from '../../choice/choice';
import { chronicle as C, hubRecord as S } from '../../../gen/strings';
import { factionName } from '../../../world';
import { groupDigits, ordinal } from '../hubstate';
import { ctlRow, esc, failed, rows, section, type Row } from '../panel';

/** The state of the last SHARE press of the card. */
export type LastShare =
  | { state: 'busy' }
  | { state: 'copied' }
  | { state: 'hand'; href: string }
  | { state: 'error'; err: unknown };

/** The inputs of the card. */
export interface LastSeasonInput {
  last: LastSeason;
  /** The chronicle volume of that season, or undefined for none. */
  volume?: ChronicleVolume;
  /** The account holds the `chronicle` key. */
  held: boolean;
  /** The address of the public reader of the volume. */
  readerHref: string;
  /** The address of the sea page of the season (R3300). */
  seaHref: string;
  share?: LastShare;
}

/** A place, with its count where the report states one. */
function placeOf(place: number, of: number): string {
  return of > 0 ? S.last_place_of({ place: ordinal(place), of }) : ordinal(place);
}

/** The rows of the card: the places, then the carry. A place of 0 is
 *  no place, and its row stands out. */
export function lastSeasonRows(l: LastSeason): Row[] {
  const out: Row[] = [[S.last_row_you, placeOf(l.rank, l.of), 'acc']];
  if (l.clanPlace > 0) out.push([S.last_row_clan, placeOf(l.clanPlace, l.clans)]);
  if (l.factionPlace > 0 && l.faction > 0) {
    out.push([factionName(l.faction).toUpperCase(), placeOf(l.factionPlace, l.factions)]);
  }
  out.push([S.last_row_honour, S.last_honour_value({ n: groupDigits(l.honour) })]);
  out.push([S.last_row_captains, l.captainsLost > 0
    ? S.last_captains_lost({ n: l.captains, lost: l.captainsLost })
    : String(l.captains)]);
  return out;
}

/** True for a mode whose closed seasons the public sea route serves: a
 *  custom world is the table of a host, and an arcade match records no
 *  frame (chronicle.md §10.6, R3300). */
export function hasPublicSea(mode: string): boolean {
  return mode !== MODE_CUSTOM && mode !== MODE_ARCADE;
}

/** THE SEA: the public reader of a shared volume, else the sea page, or ''
 *  for a mode with no public sea (R3300). */
function seaPress(o: LastSeasonInput): string {
  if (o.volume?.shared === true) return linkButton(S.last_sea, o.readerHref);
  return hasPublicSea(o.last.mode) ? linkButton(S.last_sea, o.seaHref) : '';
}

/** The presses of the card: CHRONICLE and SHARE for a volume, and THE
 *  SEA. */
function presses(o: LastSeasonInput): string {
  const v = o.volume;
  if (v === undefined || v.live) {
    const sea = seaPress(o);
    return sea === '' ? '' : ctlRow(sea);
  }
  const data = { world: String(v.world), season: String(v.season) };
  let out = button(S.last_chronicle, { act: 'lastchron', data }, { variant: 'primary' });
  if (!o.held) return ctlRow(out + seaPress(o));
  const s = o.share;
  out += s?.state === 'copied'
    ? tag(C.link_copied, 'good')
    : button(S.last_share, { act: 'lastshare', data }, { disabled: s?.state === 'busy' });
  out += seaPress(o);
  let after = '';
  if (s?.state === 'hand') {
    after = `<div class="chroncopy"><span>${esc(C.copy_by_hand)}</span>`
      + `<input type="text" readonly value="${esc(s.href)}" aria-label="${esc(C.copy_link)}"></div>`;
  } else if (s?.state === 'error') {
    after = failed(s.err);
  }
  return ctlRow(out) + after;
}

/** The LAST SEASON card, or '' for an account with no closed season. */
export function lastSeasonCard(o: LastSeasonInput | undefined): string {
  if (o === undefined) return '';
  const l = o.last;
  return section(S.card_last_season, C.volume_label({ season: l.season, world: l.worldName }),
    rows(lastSeasonRows(l)) + presses(o));
}
